import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Pause, Play, Volume2, VolumeX, Maximize2 } from "lucide-react";

interface Props {
  src: string;
  poster?: string;
  title: string;
  /** Only the main product video autoplays while visible. */
  autoplay?: boolean;
  className?: string;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const PuduVideoPlayer = ({ src, poster, title, autoplay = false, className = "" }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const manualPause = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [canAutoplay, setCanAutoplay] = useState(autoplay && !prefersReducedMotion());

  useEffect(() => {
    const el = videoRef.current;
    const wrap = wrapRef.current;
    if (!el || !wrap) return;

    el.muted = true;

    const tryPlay = () => {
      if (!canAutoplay || manualPause.current) return;
      el.muted = true;
      el.play().then(() => setPlaying(true)).catch(() => setCanAutoplay(false));
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (el.preload !== "auto") el.preload = "auto";
          tryPlay();
        } else if (!el.paused) {
          el.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.35 }
    );
    io.observe(wrap);

    const onVisibility = () => {
      if (document.hidden && !el.paused) {
        el.pause();
        setPlaying(false);
      } else if (!document.hidden) {
        tryPlay();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [canAutoplay]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      manualPause.current = false;
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      manualPause.current = true;
      el.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const goFullscreen = () => {
    const el = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else el.webkitEnterFullscreen?.();
  };

  return (
    <div
      ref={wrapRef}
      className={`relative overflow-hidden rounded-2xl border border-primary/25 bg-black/40 shadow-[0_0_40px_rgba(0,163,255,0.12)] ${className}`}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        title={title}
        aria-label={title}
        loop
        muted
        playsInline
        preload="none"
        className="w-full h-auto block"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <div className="absolute bottom-3 left-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={togglePlay}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          className="h-9 rounded-full bg-background/80 backdrop-blur"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          <span className="ml-1.5 text-xs">{playing ? "Pause" : "Play"}</span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={toggleMute}
          aria-label={muted ? `Unmute ${title}` : `Mute ${title}`}
          className="h-9 w-9 rounded-full bg-background/80 p-0 backdrop-blur"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={goFullscreen}
          aria-label={`View ${title} fullscreen`}
          className="h-9 w-9 rounded-full bg-background/80 p-0 backdrop-blur"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default PuduVideoPlayer;
